import React, { useState, useEffect, useRef, useContext } from "react";
import { Search, Filter, Calendar, User, Users, Package, Eye, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, History, AlertCircle, CheckCircle, XCircle, Clock, Settings, FileText, Download, ChevronDown, ChevronUp, ArrowRight, Shield, Building } from "lucide-react";
import axios from "axios";
import { UserContext } from "../../contexts/UserContext";

const EquipmentLogBook = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logData, setLogData] = useState([]);
  const [totalCount, setTotalCount] = useState(0); // Total count from backend pagination
  const [equipmentList, setEquipmentList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [serviceWorkshopsList, setServiceWorkshopsList] = useState([]);
  const [cooperationCompaniesList, setCooperationCompaniesList] = useState([]);  // For can_inspection=true companies
  const [inspectorCompaniesList, setInspectorCompaniesList] = useState([]);  // For can_inspection=false companies
  const [availableAssignedUsersList, setAvailableAssignedUsersList] = useState([]);  // For assignment events - users
  const [availableAssignedProjectsList, setAvailableAssignedProjectsList] = useState([]);  // For assignment events - projects
  const [availableReceivedUsersList, setAvailableReceivedUsersList] = useState([]);  // For equipment_received events - users

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState(''); // Single select - only one event type at a time
  const [inspectionSubFilter, setInspectionSubFilter] = useState([]); // Inspection subtypes filter
  const [statusSubFilter, setStatusSubFilter] = useState([]); // Status changed subtypes filter
  const [emailNotificationFilter, setEmailNotificationFilter] = useState([]); // New email notification subfilter
  const [userFilter, setUserFilter] = useState([]); // Changed to array for multi-select
  const [serviceWorkshopFilter, setServiceWorkshopFilter] = useState([]); // Service workshop subfilter
  const [serviceDirectionFilter, setServiceDirectionFilter] = useState([]); // Service direction (sent/returned) subfilter
  const [assignmentTypeFilter, setAssignmentTypeFilter] = useState([]); // Assignment type (user/project) subfilter
  const [assignedUserFilter, setAssignedUserFilter] = useState([]); // Assigned user filter for assignment events
  const [assignedProjectFilter, setAssignedProjectFilter] = useState([]); // Assigned project filter for assignment events
  const [receivedUserFilter, setReceivedUserFilter] = useState([]); // Received user filter for equipment_received events
  const [ownerCompanyFilter, setOwnerCompanyFilter] = useState([]); // Owner company filter for inspections (equipment owner)
  const [inspectorCompanyFilter, setInspectorCompanyFilter] = useState([]); // Inspector company filter for inspections (who performed inspection)
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  // Search states for checkbox filters
  const [eventTypeSearch, setEventTypeSearch] = useState('');
  const [inspectionSubSearch, setInspectionSubSearch] = useState('');
  const [statusSubSearch, setStatusSubSearch] = useState('');
  const [emailNotificationSearch, setEmailNotificationSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [serviceWorkshopSearch, setServiceWorkshopSearch] = useState('');
  const [assignedUserSearch, setAssignedUserSearch] = useState('');
  const [assignedProjectSearch, setAssignedProjectSearch] = useState('');
  const [receivedUserSearch, setReceivedUserSearch] = useState('');
  const [ownerCompanySearch, setOwnerCompanySearch] = useState('');
  const [inspectorCompanySearch, setInspectorCompanySearch] = useState('');

  // Collapse states for filters
  const [eventTypeExpanded, setEventTypeExpanded] = useState(false);
  const [inspectionSubExpanded, setInspectionSubExpanded] = useState(false);
  const [statusSubExpanded, setStatusSubExpanded] = useState(false);
  const [emailNotificationExpanded, setEmailNotificationExpanded] = useState(false);
  const [userExpanded, setUserExpanded] = useState(false);
  const [serviceWorkshopExpanded, setServiceWorkshopExpanded] = useState(false);
  const [serviceDirectionExpanded, setServiceDirectionExpanded] = useState(false);
  const [assignmentTypeExpanded, setAssignmentTypeExpanded] = useState(false);
  const [assignedUserExpanded, setAssignedUserExpanded] = useState(false);
  const [assignedProjectExpanded, setAssignedProjectExpanded] = useState(false);
  const [receivedUserExpanded, setReceivedUserExpanded] = useState(false);
  const [ownerCompanyExpanded, setOwnerCompanyExpanded] = useState(false);
  const [inspectorCompanyExpanded, setInspectorCompanyExpanded] = useState(false);

  // Refs for click-outside functionality
  const eventTypeRef = useRef(null);
  const inspectionSubRef = useRef(null);
  const statusSubRef = useRef(null);
  const emailNotificationRef = useRef(null);
  const userRef = useRef(null);
  const serviceWorkshopRef = useRef(null);
  const serviceDirectionRef = useRef(null);
  const assignmentTypeRef = useRef(null);
  const assignedUserRef = useRef(null);
  const assignedProjectRef = useRef(null);
  const receivedUserRef = useRef(null);
  const ownerCompanyRef = useRef(null);
  const inspectorCompanyRef = useRef(null);

  // Ref for search input to maintain focus
  const searchInputRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Selection for reports - now tracks individual entries, not equipment IDs
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

  // Event types for PPE equipment
  const eventTypes = {
    'inspection': { label: 'Inspection', color: 'text-purple-600', icon: Calendar, isGroup: true },
    'inspection_passed': { label: 'Inspection Passed', color: 'text-green-600', icon: CheckCircle, parent: 'inspection' },
    'inspection_failed': { label: 'Inspection Failed', color: 'text-red-600', icon: XCircle, parent: 'inspection' },
    'assignment_created': { label: 'Equipment Assigned', color: 'text-brand-600', icon: User },
    'assignment_returned': { label: 'Equipment Returned', color: 'text-green-600', icon: ArrowRight },
    'equipment_received': { label: 'Equipment Received', color: 'text-cyan-600', icon: CheckCircle },
    'service_shipment': { label: 'Service Shipment', color: 'text-red-500', icon: Package, isGroup: true },
    'email_notifications': { label: 'Email Notifications', color: 'text-indigo-600', icon: AlertCircle, isGroup: true },
    'inspection_expiry': { label: 'Inspection Expiry Notification', color: 'text-orange-600', icon: AlertCircle, parent: 'email_notifications' },
    'manufacturer_expiry': { label: 'Manufacturer Expiry Notification', color: 'text-purple-600', icon: AlertCircle, parent: 'email_notifications' },
    'equipment_created': { label: 'Equipment Added', color: 'text-brand-600', icon: Package },
    'equipment_updated': { label: 'Equipment Updated', color: 'text-yellow-600', icon: Settings },
    'equipment_expired': { label: 'Equipment Expired', color: 'text-orange-600', icon: Clock },
    'equipment_damaged': { label: 'Equipment Damaged', color: 'text-red-600', icon: AlertCircle },
    'equipment_deleted': { label: 'Equipment Deleted', color: 'text-slate-600', icon: XCircle },
    'equipment_restored': { label: 'Equipment Restored', color: 'text-green-600', icon: CheckCircle },
    'status_changed': { label: 'Status Changed', color: 'text-orange-600', icon: Settings, isGroup: true },
    'maintenance_scheduled': { label: 'Maintenance Scheduled', color: 'text-purple-600', icon: Calendar },
  };

  // Inspection subtypes
  const inspectionSubtypes = {
    'inspection_passed': { label: 'Passed', color: 'text-green-600', icon: CheckCircle },
    'inspection_failed': { label: 'Failed', color: 'text-red-600', icon: XCircle },
  };

  // Status changed subtypes (equipment statuses - "to" status)
  const statusSubtypes = {
    'available': { label: 'To Available', color: 'text-green-600' },
    'service': { label: 'To Service', color: 'text-orange-600' },
    'reservation': { label: 'To Reservation', color: 'text-brand-600' },
    'issued': { label: 'To Issued', color: 'text-purple-600' },
    'damaged': { label: 'To Damaged', color: 'text-red-600' },
    'out_of_date': { label: 'To Out of Date', color: 'text-slate-600' },
    'received': { label: 'To Received', color: 'text-indigo-600' },
    'returned': { label: 'To Returned', color: 'text-teal-600' },
  };

  // Email notification subtypes
  const emailNotificationTypes = {
    'inspection_expiry': { label: 'Inspection Expiry', color: 'text-orange-600' },
    'manufacturer_expiry': { label: 'Manufacturer Expiry', color: 'text-purple-600' },
  };

  useEffect(() => {
    // First fetch support data (equipment and users), then log data
    const initializeData = async () => {
      await fetchSupportData();
      await fetchLogData();
    };
    initializeData();
  }, []);

  // Debounce search term (wait 3000ms after user stops typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Store whether search input had focus before update
      const hadFocus = document.activeElement === searchInputRef.current;

      setDebouncedSearchTerm(searchTerm);

      // Restore focus after state update if it had focus
      if (hadFocus) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }, 3000); // 3000ms delay (3 seconds) - encourages users to press Enter for immediate search

    return () => clearTimeout(timer); // Cleanup on each keystroke
  }, [searchTerm]);

  // Handle Enter key to immediately trigger search (bypass debounce)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setDebouncedSearchTerm(searchTerm); // Immediately update debounced term
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    dateFromFilter,
    dateToFilter,
    sortBy,
    sortOrder,
    eventTypeFilter,
    userFilter,
    emailNotificationFilter,
    debouncedSearchTerm,
    itemsPerPage
  ]);

  // Fetch data when page or filters change
  useEffect(() => {
    fetchLogData();
  }, [
    currentPage,
    itemsPerPage,
    dateFromFilter,
    dateToFilter,
    sortBy,
    sortOrder,
    eventTypeFilter,
    userFilter,
    emailNotificationFilter,
    inspectionSubFilter,
    statusSubFilter,
    serviceWorkshopFilter,
    serviceDirectionFilter,
    assignmentTypeFilter,
    assignedUserFilter,
    assignedProjectFilter,
    receivedUserFilter,
    ownerCompanyFilter,
    inspectorCompanyFilter,
    debouncedSearchTerm
  ]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (eventTypeRef.current && !eventTypeRef.current.contains(event.target)) {
        setEventTypeExpanded(false);
      }
      if (inspectionSubRef.current && !inspectionSubRef.current.contains(event.target)) {
        setInspectionSubExpanded(false);
      }
      if (statusSubRef.current && !statusSubRef.current.contains(event.target)) {
        setStatusSubExpanded(false);
      }
      if (userRef.current && !userRef.current.contains(event.target)) {
        setUserExpanded(false);
      }
      if (emailNotificationRef.current && !emailNotificationRef.current.contains(event.target)) {
        setEmailNotificationExpanded(false);
      }
      if (serviceWorkshopRef.current && !serviceWorkshopRef.current.contains(event.target)) {
        setServiceWorkshopExpanded(false);
      }
      if (serviceDirectionRef.current && !serviceDirectionRef.current.contains(event.target)) {
        setServiceDirectionExpanded(false);
      }
      if (assignmentTypeRef.current && !assignmentTypeRef.current.contains(event.target)) {
        setAssignmentTypeExpanded(false);
      }
      if (assignedUserRef.current && !assignedUserRef.current.contains(event.target)) {
        setAssignedUserExpanded(false);
      }
      if (assignedProjectRef.current && !assignedProjectRef.current.contains(event.target)) {
        setAssignedProjectExpanded(false);
      }
      if (receivedUserRef.current && !receivedUserRef.current.contains(event.target)) {
        setReceivedUserExpanded(false);
      }
      if (ownerCompanyRef.current && !ownerCompanyRef.current.contains(event.target)) {
        setOwnerCompanyExpanded(false);
      }
      if (inspectorCompanyRef.current && !inspectorCompanyRef.current.contains(event.target)) {
        setInspectorCompanyExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchLogData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Build query parameters - ALL filters now sent to backend
      const params = new URLSearchParams();

      // Pagination parameters
      params.append('page', currentPage);
      params.append('page_size', itemsPerPage);

      // Date filters
      if (dateFromFilter) {
        params.append('date_from', dateFromFilter);
      }
      if (dateToFilter) {
        // Add one day to include the end date in the range
        const endDate = new Date(dateToFilter);
        endDate.setDate(endDate.getDate() + 1);
        params.append('date_to', endDate.toISOString().split('T')[0]);
      }

      // Sorting
      if (sortBy) {
        params.append('ordering', sortOrder === 'desc' ? `-${sortBy}` : sortBy);
      }

      // Single-select event type filter
      if (eventTypeFilter) {
        params.append('event_type', eventTypeFilter);
      }

      // Multi-select user filter
      if (userFilter.length > 0) {
        userFilter.forEach(userId => params.append('user_id', userId));
      }

      // Email notification subtype filter (for email_notification events)
      if (emailNotificationFilter.length > 0) {
        emailNotificationFilter.forEach(type => params.append('notification_type', type));
      }

      // Inspection result filter (for inspection events)
      if (inspectionSubFilter.length > 0) {
        inspectionSubFilter.forEach(result => params.append('inspection_result', result));
      }

      // Status change filter (to_status)
      if (statusSubFilter.length > 0) {
        statusSubFilter.forEach(status => params.append('status_to', status));
      }

      // Service workshop filter
      if (serviceWorkshopFilter.length > 0) {
        serviceWorkshopFilter.forEach(companyId => params.append('service_company', companyId));
      }

      // Service direction filter (sent/returned)
      if (serviceDirectionFilter.length > 0) {
        serviceDirectionFilter.forEach(direction => params.append('service_direction', direction));
      }

      // Assignment type filter (user/project)
      if (assignmentTypeFilter.length > 0) {
        assignmentTypeFilter.forEach(type => params.append('assignment_type', type));
      }

      // Assigned user filter (for assignment events)
      if (assignedUserFilter.length > 0) {
        assignedUserFilter.forEach(userId => params.append('assigned_user', userId));
      }

      // Assigned project filter (for assignment events)
      if (assignedProjectFilter.length > 0) {
        assignedProjectFilter.forEach(projectId => params.append('assigned_project', projectId));
      }

      // Received user filter (for equipment_received events)
      if (receivedUserFilter.length > 0) {
        receivedUserFilter.forEach(userId => params.append('received_user', userId));
      }

      // Owner company filter (for inspections - equipment owner)
      if (ownerCompanyFilter.length > 0) {
        ownerCompanyFilter.forEach(companyId => params.append('owner_company', companyId));
      }

      // Inspector company filter (for inspections - who performed inspection)
      if (inspectorCompanyFilter.length > 0) {
        inspectorCompanyFilter.forEach(companyId => params.append('inspector_company', companyId));
      }

      // Text search (use debounced term)
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.append('search', debouncedSearchTerm.trim());
      }

      // Fetch equipment logs and service companies
      const [equipmentLogResponse, serviceCompaniesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/equipment-log/?${params.toString()}`, config),
        axios.get(`${API_BASE_URL}/servicecompany/`, config)
      ]);

      const equipmentLogData = equipmentLogResponse.data.results || equipmentLogResponse.data || [];
      const serviceCompaniesData = serviceCompaniesResponse.data.results || serviceCompaniesResponse.data || [];

      // All logs (including email notifications) are now in EquipmentEventLog
      setLogData(equipmentLogData);

      // Store pagination info from backend
      const count = equipmentLogResponse.data.count || 0;
      setTotalCount(count);
      setTotalPages(count ? Math.ceil(count / itemsPerPage) : 1);

      // Set service companies list (from backend, not from logs)
      setServiceWorkshopsList(serviceCompaniesData);

      // Set available assigned users and projects (from backend, only when assignment events are selected)
      if (equipmentLogResponse.data.available_assigned_users) {
        setAvailableAssignedUsersList(equipmentLogResponse.data.available_assigned_users);
      } else {
        setAvailableAssignedUsersList([]);
      }
      if (equipmentLogResponse.data.available_assigned_projects) {
        setAvailableAssignedProjectsList(equipmentLogResponse.data.available_assigned_projects);
      } else {
        setAvailableAssignedProjectsList([]);
      }

      // Set available received users (from backend, only when equipment_received event is selected)
      if (equipmentLogResponse.data.available_received_users) {
        setAvailableReceivedUsersList(equipmentLogResponse.data.available_received_users);
      } else {
        setAvailableReceivedUsersList([]);
      }

    } catch (error) {
      console.error('Error fetching log data:', error);
      setError('Failed to fetch log data');
      // For demo purposes, create mock data structure
      const mockData = createMockLogData();
      setLogData(mockData);
      
      // Service companies will be empty in case of error
      setServiceWorkshopsList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      console.log('🔍 User from UserContext:', user);
      console.log('🔍 company_can_inspection value:', user?.company_can_inspection);

      const requests = [
        axios.get(`${API_BASE_URL}/safetyequipment/`, config),
        axios.get(`${API_BASE_URL}/equipment-log/available-users/`, config)  // Get only users who have created log entries
      ];

      // Fetch appropriate companies list based on can_inspection
      if (user?.company_can_inspection === true) {
        // For companies WITH can_inspection: fetch companies they inspect FOR
        console.log('✅ Fetching cooperation companies (for "Inspection for Company" filter)');
        requests.push(axios.get(`${API_BASE_URL}/cooperating-companies/manage/`, config));
      } else {
        // For companies WITHOUT can_inspection: fetch both types of companies
        console.log('✅ Fetching inspector companies (for "Inspection from Company" filter)');
        requests.push(axios.get(`${API_BASE_URL}/cooperating-companies/available/`, config));  // Companies that inspect FOR us
        requests.push(axios.get(`${API_BASE_URL}/shared-equipment-companies/`, config));  // Companies we share equipment with
      }

      const responses = await Promise.all(requests);
      const equipmentData = responses[0].data || [];
      const usersData = responses[1].data || [];

      console.log('✅ Fetched data from endpoints');

      setEquipmentList(equipmentData);
      setUsersList(usersData);

      // Set appropriate list based on can_inspection
      if (user?.company_can_inspection === true) {
        const companiesData = responses[2] ? (responses[2].data || []) : [];
        setCooperationCompaniesList(companiesData);
        setInspectorCompaniesList([]);
        console.log('📋 Set cooperationCompaniesList:', companiesData.length, 'companies');
      } else {
        // For companies WITHOUT can_inspection, combine multiple sources
        const inspectionCompaniesData = responses[2] ? (responses[2].data || []) : [];  // External inspection companies
        const sharedCompaniesData = responses[3] ? (responses[3].data || []) : [];  // Companies we share with

        console.log('📋 Inspection companies:', inspectionCompaniesData.length);
        console.log('📋 Shared equipment companies:', sharedCompaniesData.length);

        let inspectorsList = [];

        // 1. Add own company first
        if (user?.company && user?.company_name) {
          inspectorsList.push({
            id: user.company,
            name: `${user.company_name} (My Company)`
          });
        }

        // 2. Add external inspection companies with label
        inspectionCompaniesData.forEach(company => {
          if (!inspectorsList.find(c => c.id === company.id)) {
            inspectorsList.push({
              id: company.id,
              name: `${company.name} (External Inspection)`
            });
          }
        });

        // 3. Add companies we share equipment with (they can also inspect)
        sharedCompaniesData.forEach(company => {
          if (!inspectorsList.find(c => c.id === company.id)) {
            inspectorsList.push({
              id: company.id,
              name: `${company.name} (Shared Equipment)`
            });
          }
        });

        setCooperationCompaniesList([]);
        setInspectorCompaniesList(inspectorsList);
        console.log('📋 Set inspectorCompaniesList:', inspectorsList.length, 'companies total');
      }

    } catch (error) {
      console.error('Error fetching support data:', error);
    }
  };

  // Create mock log data based on existing equipment data
  const createMockLogData = () => {
    const mockData = [];
    const now = new Date();
    
    // Get available equipment and users for realistic mock data
    const availableEquipment = equipmentList.length > 0 ? equipmentList : [];
    const availableUsers = usersList.length > 0 ? usersList : [];
    
    // Simulate various events for different equipment
    for (let i = 0; i < 50; i++) {
      // Use real equipment if available, otherwise fallback
      let equipment, equipmentName, equipmentSerial;
      
      if (availableEquipment.length > 0) {
        equipment = availableEquipment[Math.floor(Math.random() * availableEquipment.length)];
        // Create more detailed equipment name including model info
        let equipmentName = equipment.safety_equipment_name || `Equipment ${equipment.id}`;
        
        // Add model information if available
        if (equipment.safety_equipment_model && equipment.safety_equipment_model.name) {
          equipmentName = `${equipment.safety_equipment_model.brand || ''} ${equipment.safety_equipment_model.name}`.trim();
        } else if (equipment.model_name) {
          equipmentName = equipment.model_name;
        }
        
        equipmentSerial = equipment.serial_number || equipment.safety_equipment_serial_number || `EQ${equipment.id.toString().padStart(4, '0')}`;
      } else {
        const equipmentId = Math.floor(Math.random() * 10) + 1;
        equipmentName = `Safety Equipment ${equipmentId}`;
        equipmentSerial = `SE${equipmentId.toString().padStart(4, '0')}`;
        equipment = { id: equipmentId };
      }
      
      // Use real users if available, otherwise fallback
      let user, userName;
      if (availableUsers.length > 0) {
        user = availableUsers[Math.floor(Math.random() * availableUsers.length)];
        userName = `${user.first_name} ${user.last_name}`.trim();
      } else {
        const userId = Math.floor(Math.random() * 5) + 1;
        userName = `User ${userId}`;
        user = { id: userId };
      }
      
      const eventTypeKeys = Object.keys(eventTypes);
      const eventType = eventTypeKeys[Math.floor(Math.random() * eventTypeKeys.length)];
      const daysAgo = Math.floor(Math.random() * 90);
      const timestamp = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
      
      // For inspection events, use inspector names instead of regular user names
      if (eventType === 'inspection_passed' || eventType === 'inspection_failed') {
        const inspectors = ['Vulcan Krakatau', 'Szymon Szymanski', 'Inspector Smith', 'Safety Expert Jones'];
        userName = inspectors[Math.floor(Math.random() * inspectors.length)];
      }
      
      mockData.push({
        id: i + 1,
        equipment_id: equipment.id,
        equipment_name: equipmentName,
        equipment_serial: equipmentSerial,
        equipment_model: availableEquipment.length > 0 && equipment.safety_equipment_model ? 
          `${equipment.safety_equipment_model.brand || ''} ${equipment.safety_equipment_model.name || ''}`.trim() : '',
        equipment_type: availableEquipment.length > 0 && equipment.safety_equipment_type ? 
          equipment.safety_equipment_type.name : '',
        event_type: eventType,
        timestamp: timestamp.toISOString(),
        user_name: userName,
        user_id: user.id,
        description: getEventDescription(eventType, equipmentName),
        details: getEventDetails(eventType),
        ip_address: `192.168.1.${Math.floor(Math.random() * 254) + 1}`,
      });
    }
    
    return mockData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const getEventDescription = (eventType, equipmentName) => {
    switch (eventType) {
      case 'assignment_created': return `${equipmentName} assigned to user`;
      case 'assignment_returned': return `${equipmentName} returned from assignment`;
      case 'equipment_received': return `${equipmentName} received by user`;
      case 'equipment_deleted': return `${equipmentName} deleted from inventory`;
      case 'equipment_restored': return `${equipmentName} restored from deleted status`;
      case 'equipment_damaged': return `${equipmentName} marked as damaged`;
      case 'equipment_expired': return `${equipmentName} marked as expired`;
      case 'inspection_passed': return `${equipmentName} passed safety inspection`;
      case 'inspection_failed': return `${equipmentName} failed safety inspection`;
      case 'status_changed': return `${equipmentName} status updated`;
      case 'equipment_created': return `${equipmentName} added to inventory`;
      case 'equipment_updated': return `${equipmentName} information updated`;
      case 'maintenance_scheduled': return `Maintenance scheduled for ${equipmentName}`;
      case 'service_shipment': return `${equipmentName} sent to service workshop`;
      case 'validity_expiring': return `${equipmentName} validity period expiring soon`;
      default: return `Event occurred for ${equipmentName}`;
    }
  };

  const getEventDetails = (eventType) => {
    switch (eventType) {
      case 'assignment_created': return { from_status: 'available', to_status: 'assigned', duration: null };
      case 'assignment_returned': return { from_status: 'assigned', to_status: 'available', duration: '15 days' };
      case 'equipment_received': return { from_status: 'issued', to_status: 'received' };
      case 'equipment_deleted': return { from_status: 'any', to_status: 'deleted' };
      case 'equipment_damaged': return { from_status: 'any', to_status: 'damaged' };
      case 'equipment_expired': return { from_status: 'any', to_status: 'out_of_date' };
      case 'inspection_passed': return { result: 'pass', next_inspection: '2025-01-15' };
      case 'inspection_failed': return { result: 'fail', issues: 'Wear detected, needs replacement' };
      case 'status_changed': return { from: 'available', to: 'service' };
      case 'maintenance_scheduled': return { scheduled_date: '2025-01-20', type: 'routine' };
      case 'service_shipment': 
        const serviceWorkshops = ['TechService Pro', 'SafetyEquip Repair', 'Industrial Service Co', 'QuickFix Solutions'];
        const randomWorkshop = serviceWorkshops[Math.floor(Math.random() * serviceWorkshops.length)];
        return { 
          service_workshop: randomWorkshop, 
          tracking_number: `SRV-2025-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}` 
        };
      default: return {};
    }
  };

  // All filtering and pagination is now done on the backend
  // logData already contains filtered and paginated results
  const paginatedData = logData;

  // Sorting logic
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setEventTypeFilter(''); // Reset to empty string
    setInspectionSubFilter([]); // Reset inspection subfilter
    setStatusSubFilter([]); // Reset status subfilter
    setEmailNotificationFilter([]); // Reset email notification filter
    setUserFilter([]); // Reset to empty array
    setServiceWorkshopFilter([]); // Reset service workshop filter
    setServiceDirectionFilter([]); // Reset service direction filter
    setAssignmentTypeFilter([]); // Reset assignment type filter
    setAssignedUserFilter([]); // Reset assigned user filter
    setAssignedProjectFilter([]); // Reset assigned project filter
    setReceivedUserFilter([]); // Reset received user filter
    setOwnerCompanyFilter([]); // Reset owner company filter
    setInspectorCompanyFilter([]); // Reset inspector company filter
    setDateFromFilter('');
    setDateToFilter('');
    setEventTypeSearch('');
    setInspectionSubSearch(''); // Reset inspection subsearch
    setStatusSubSearch(''); // Reset status subsearch
    setEmailNotificationSearch(''); // Reset email notification search
    setUserSearch('');
    setServiceWorkshopSearch(''); // Reset service workshop search
    setAssignedUserSearch(''); // Reset assigned user search
    setAssignedProjectSearch(''); // Reset assigned project search
    setReceivedUserSearch(''); // Reset received user search
    setOwnerCompanySearch(''); // Reset owner company search
    setInspectorCompanySearch(''); // Reset inspector company search
    setEventTypeExpanded(false);
    setInspectionSubExpanded(false); // Reset inspection subexpanded
    setStatusSubExpanded(false); // Reset status subexpanded
    setEmailNotificationExpanded(false); // Reset email notification expanded state
    setUserExpanded(false);
    setServiceWorkshopExpanded(false); // Reset service workshop expanded state
    setServiceDirectionExpanded(false); // Reset service direction expanded state
    setAssignmentTypeExpanded(false); // Reset assignment type expanded state
    setAssignedUserExpanded(false); // Reset assigned user expanded state
    setAssignedProjectExpanded(false); // Reset assigned project expanded state
    setReceivedUserExpanded(false); // Reset received user expanded state
    setOwnerCompanyExpanded(false); // Reset owner company expanded state
    setInspectorCompanyExpanded(false); // Reset inspector company expanded state
    setCurrentPage(1);
  };

  // Helper functions for single-select filters (with deselect capability)
  const handleEventTypeToggle = (eventTypeKey) => {
    setEventTypeFilter(prev => {
      // If clicking the same option, deselect it
      if (prev === eventTypeKey) {
        // Clear subfilters when deselecting
        if (eventTypeKey === 'inspection') {
          setInspectionSubFilter([]);
          setOwnerCompanyFilter([]);
          setInspectorCompanyFilter([]);
        }
        if (eventTypeKey === 'status_changed') {
          setStatusSubFilter([]);
        }
        if (eventTypeKey === 'email_notifications') {
          setEmailNotificationFilter([]);
        }
        if (eventTypeKey === 'service_shipment') {
          setServiceWorkshopFilter([]);
          setServiceDirectionFilter([]);
        }
        if (eventTypeKey === 'assignment_created' || eventTypeKey === 'assignment_returned') {
          setAssignmentTypeFilter([]);
          setAssignedUserFilter([]);
          setAssignedProjectFilter([]);
        }
        if (eventTypeKey === 'equipment_received') {
          setReceivedUserFilter([]);
        }
        return ''; // Deselect
      } else {
        // Switching to a different option - clear old subfilters
        if (prev === 'inspection') {
          setInspectionSubFilter([]);
          setOwnerCompanyFilter([]);
          setInspectorCompanyFilter([]);
        }
        if (prev === 'status_changed') {
          setStatusSubFilter([]);
        }
        if (prev === 'email_notifications') {
          setEmailNotificationFilter([]);
        }
        if (prev === 'service_shipment') {
          setServiceWorkshopFilter([]);
          setServiceDirectionFilter([]);
        }
        if (prev === 'assignment_created' || prev === 'assignment_returned') {
          setAssignmentTypeFilter([]);
          setAssignedUserFilter([]);
          setAssignedProjectFilter([]);
        }
        if (prev === 'equipment_received') {
          setReceivedUserFilter([]);
        }
        return eventTypeKey; // Select new option
      }
    });

    // Close dropdown after selection
    setEventTypeExpanded(false);
  };

  const handleEmailNotificationToggle = (notificationTypeKey) => {
    setEmailNotificationFilter(prev => {
      if (prev.includes(notificationTypeKey)) {
        return prev.filter(key => key !== notificationTypeKey);
      } else {
        return [...prev, notificationTypeKey];
      }
    });
  };

  const handleInspectionSubToggle = (inspectionTypeKey) => {
    setInspectionSubFilter(prev => {
      if (prev.includes(inspectionTypeKey)) {
        return prev.filter(key => key !== inspectionTypeKey);
      } else {
        return [...prev, inspectionTypeKey];
      }
    });
  };

  const handleStatusSubToggle = (statusKey) => {
    setStatusSubFilter(prev => {
      if (prev.includes(statusKey)) {
        return prev.filter(key => key !== statusKey);
      } else {
        return [...prev, statusKey];
      }
    });
  };

  const handleUserToggle = (userId) => {
    setUserFilter(prev => {
      // Handle both 'system' string and numeric IDs
      const userIdStr = userId === 'system' ? 'system' : userId.toString();
      if (prev.includes(userIdStr)) {
        return prev.filter(id => id !== userIdStr);
      } else {
        return [...prev, userIdStr];
      }
    });
  };

  // Filter functions for search within checkboxes
  const getFilteredEventTypes = () => {
    return Object.entries(eventTypes).filter(([key, type]) => {
      // Exclude child email notification types from main event type filter
      if (type.parent) return false;
      return type.label.toLowerCase().includes(eventTypeSearch.toLowerCase());
    });
  };

  const getFilteredEmailNotificationTypes = () => {
    return Object.entries(emailNotificationTypes).filter(([key, type]) =>
      type.label.toLowerCase().includes(emailNotificationSearch.toLowerCase())
    );
  };

  const getFilteredUsers = () => {
    return usersList.filter(user => {
      const fullName = (user.full_name || `${user.first_name} ${user.last_name}`).toLowerCase();
      return fullName.includes(userSearch.toLowerCase());
    });
  };

  const handleServiceWorkshopToggle = (workshopId) => {
    setServiceWorkshopFilter(prev => {
      if (prev.includes(workshopId)) {
        return prev.filter(id => id !== workshopId);
      } else {
        return [...prev, workshopId];
      }
    });
  };

  const handleServiceDirectionToggle = (direction) => {
    setServiceDirectionFilter(prev => {
      if (prev.includes(direction)) {
        return prev.filter(d => d !== direction);
      } else {
        return [...prev, direction];
      }
    });
  };

  const handleAssignmentTypeToggle = (type) => {
    setAssignmentTypeFilter(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleAssignedUserToggle = (userId) => {
    setAssignedUserFilter(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleAssignedProjectToggle = (projectId) => {
    setAssignedProjectFilter(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleReceivedUserToggle = (userId) => {
    setReceivedUserFilter(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const getFilteredServiceWorkshops = () => {
    return serviceWorkshopsList.filter(company =>
      company.name && company.name.toLowerCase().includes(serviceWorkshopSearch.toLowerCase())
    );
  };

  const handleOwnerCompanyToggle = (companyId) => {
    setOwnerCompanyFilter(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const getFilteredOwnerCompanies = () => {
    return cooperationCompaniesList.filter(company =>
      company.name.toLowerCase().includes(ownerCompanySearch.toLowerCase())
    );
  };

  const handleInspectorCompanyToggle = (companyId) => {
    setInspectorCompanyFilter(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const getFilteredInspectorCompanies = () => {
    // For companies WITHOUT can_inspection, show companies that perform inspections FOR them
    // This uses the inspectorCompaniesList which is fetched from /cooperating-companies/available/
    return inspectorCompaniesList.filter(company =>
      company.name.toLowerCase().includes(inspectorCompanySearch.toLowerCase())
    );
  };

  // Get unique equipment from current page data for selection
  const getUniqueEquipment = () => {
    const equipmentMap = new Map();
    logData.forEach(entry => {
      if (!equipmentMap.has(entry.equipment_id)) {
        equipmentMap.set(entry.equipment_id, {
          id: entry.equipment_id,
          name: entry.equipment_name,
          serial: entry.equipment_serial
        });
      }
    });
    return Array.from(equipmentMap.values());
  };

  const uniqueEquipment = getUniqueEquipment();

  // Selection handlers - now work with individual entries
  const handleEntrySelect = (entryId) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
    setSelectAll(newSelected.size === paginatedData.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEntries(new Set());
      setSelectAll(false);
    } else {
      const allEntryIds = paginatedData.map(entry => entry.id);
      setSelectedEntries(new Set(allEntryIds));
      setSelectAll(true);
    }
  };

  // Get unique equipment IDs from selected entries for PDF generation
  const getSelectedEquipmentIds = () => {
    const equipmentIds = new Set();
    selectedEntries.forEach(entryId => {
      const entry = logData.find(e => e.id === entryId);
      if (entry) {
        equipmentIds.add(entry.equipment_id);
      }
    });
    return Array.from(equipmentIds);
  };

  // Check if Inspection Cost button should be shown
  const shouldShowInspectionCostButton = () => {
    if (!user?.company_can_inspection) {
      return false;
    }

    // Check if any selected entries are inspections
    let hasInspection = false;
    selectedEntries.forEach(entryId => {
      const entry = logData.find(e => e.id === entryId);
      if (entry && ['inspection', 'inspection_passed', 'inspection_failed'].includes(entry.event_type)) {
        hasInspection = true;
      }
    });

    return hasInspection;
  };

  // Get all available report buttons based on selected entries
  const getAvailableReportButtons = () => {
    if (selectedEntries.size === 0) {
      return [];
    }

    const reportButtons = [];
    const eventTypeCounts = {};

    // Count event types in selected entries
    selectedEntries.forEach(entryId => {
      const entry = logData.find(e => e.id === entryId);
      if (entry) {
        eventTypeCounts[entry.event_type] = (eventTypeCounts[entry.event_type] || 0) + 1;
      }
    });

    // Check for inspection events
    const inspectionCount = (eventTypeCounts['inspection'] || 0) +
                           (eventTypeCounts['inspection_passed'] || 0) +
                           (eventTypeCounts['inspection_failed'] || 0);
    if (inspectionCount > 0) {
      reportButtons.push({
        type: 'inspection',
        label: `Inspection Report (${inspectionCount})`,
        eventTypes: ['inspection', 'inspection_passed', 'inspection_failed'],
        count: inspectionCount
      });
    }

    // Check for assignment returned events
    const returnedCount = eventTypeCounts['assignment_returned'] || 0;
    if (returnedCount > 0) {
      reportButtons.push({
        type: 'returned',
        label: `Returned Report (${returnedCount})`,
        eventTypes: ['assignment_returned'],
        count: returnedCount
      });
    }

    // Check for assignment created events
    const assignmentCount = eventTypeCounts['assignment_created'] || 0;
    if (assignmentCount > 0) {
      reportButtons.push({
        type: 'assignment',
        label: `Assignment Report (${assignmentCount})`,
        eventTypes: ['assignment_created'],
        count: assignmentCount
      });
    }

    // Check for equipment received events
    const receivedCount = eventTypeCounts['equipment_received'] || 0;
    if (receivedCount > 0) {
      reportButtons.push({
        type: 'equipment_received',
        label: `Received Report (${receivedCount})`,
        eventTypes: ['equipment_received'],
        count: receivedCount
      });
    }

    // Check for equipment created events
    const createdCount = eventTypeCounts['equipment_created'] || 0;
    if (createdCount > 0) {
      reportButtons.push({
        type: 'equipment_created',
        label: `Added Report (${createdCount})`,
        eventTypes: ['equipment_created'],
        count: createdCount
      });
    }

    // Check for equipment expired events
    const expiredCount = eventTypeCounts['equipment_expired'] || 0;
    if (expiredCount > 0) {
      reportButtons.push({
        type: 'equipment_expired',
        label: `Expired Report (${expiredCount})`,
        eventTypes: ['equipment_expired'],
        count: expiredCount
      });
    }

    // Check for equipment damaged events
    const damagedCount = eventTypeCounts['equipment_damaged'] || 0;
    if (damagedCount > 0) {
      reportButtons.push({
        type: 'equipment_damaged',
        label: `Damaged Report (${damagedCount})`,
        eventTypes: ['equipment_damaged'],
        count: damagedCount
      });
    }

    // Check for equipment deleted events
    const deletedCount = eventTypeCounts['equipment_deleted'] || 0;
    if (deletedCount > 0) {
      reportButtons.push({
        type: 'equipment_deleted',
        label: `Deleted Report (${deletedCount})`,
        eventTypes: ['equipment_deleted'],
        count: deletedCount
      });
    }

    // Check for equipment restored events
    const restoredCount = eventTypeCounts['equipment_restored'] || 0;
    if (restoredCount > 0) {
      reportButtons.push({
        type: 'equipment_restored',
        label: `Restored Report (${restoredCount})`,
        eventTypes: ['equipment_restored'],
        count: restoredCount
      });
    }

    return reportButtons;
  };

  // Generate Inspection Cost Report handler
  const handleGenerateInspectionCostReport = async () => {
    // Get all selected inspection entries
    const filteredEntries = [];
    const inspectionIds = [];

    selectedEntries.forEach(entryId => {
      const entry = logData.find(e => e.id === entryId);
      if (entry && ['inspection', 'inspection_passed', 'inspection_failed'].includes(entry.event_type)) {
        filteredEntries.push(entry);
        if (entry.inspection_id) {
          inspectionIds.push(entry.inspection_id);
        }
      }
    });

    if (inspectionIds.length === 0) {
      alert('No valid inspection entries found.');
      return;
    }

    try {
      setIsGeneratingReport(true);
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      };

      const reportType = filteredEntries.length === 1 ? 'single' : 'multiple';

      const reportPayload = {
        inspection_ids: inspectionIds,
        report_type: reportType,
        report_category: 'inspection_cost',
        document_title: 'INSPECTION COSTS',
        title: 'INSPECTION COSTS',
      };

      console.log('📤 Sending inspection cost report request:', reportPayload);

      const response = await axios.post(`${API_BASE_URL}/ppe-report/`, reportPayload, config);

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = filteredEntries.length === 1
        ? `PPE_Inspection_Cost_${filteredEntries[0].equipment_id}_${timestamp}.pdf`
        : `PPE_Inspection_Cost_${timestamp}.pdf`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // Clear selection after successful generation
      setSelectedEntries(new Set());
      setSelectAll(false);

    } catch (error) {
      console.error('Error generating inspection cost report:', error);
      alert('Failed to generate inspection cost report. Please try again.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Generate Report handler - takes reportInfo as parameter
  const handleGenerateReport = async (reportInfo) => {
    if (!reportInfo) {
      alert('Please select entries to generate a report.');
      return;
    }

    // Filter selected entries to only include matching event types
    const filteredEntries = [];
    const equipmentIds = new Set();
    const inspectionIds = [];
    const assignmentIds = [];
    const eventIds = [];

    selectedEntries.forEach(entryId => {
      const entry = logData.find(e => e.id === entryId);
      if (entry && reportInfo.eventTypes.includes(entry.event_type)) {
        filteredEntries.push(entry);
        equipmentIds.add(entry.equipment_id);

        // Extract numeric ID from composite ID (e.g., 'equipment_created_133382' -> 133382)
        const numericId = entry.id.toString().split('_').pop();
        eventIds.push(parseInt(numericId)); // Collect event IDs for new report types

        // Collect inspection IDs for inspection reports
        if (reportInfo.type === 'inspection' && entry.inspection_id) {
          inspectionIds.push(entry.inspection_id);
        }

        // Collect assignment IDs for assignment/returned/equipment_received reports
        if ((reportInfo.type === 'assignment' || reportInfo.type === 'returned' || reportInfo.type === 'equipment_received') && entry.assignment_id) {
          assignmentIds.push(entry.assignment_id);
        }
      }
    });

    if (filteredEntries.length === 0) {
      alert('No valid entries found for the selected report type.');
      return;
    }

    try {
      setIsGeneratingReport(true);
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob' // Important for PDF download
      };

      const reportType = filteredEntries.length === 1 ? 'single' : 'multiple';

      // Determine report category based on button type
      let reportCategory = 'equipment';
      let documentTitle = 'EQUIPMENT REPORT';

      if (reportInfo.type === 'inspection') {
        reportCategory = 'inspection';
        documentTitle = 'INSPECTION CERTIFICATES';
      } else if (reportInfo.type === 'returned') {
        reportCategory = 'returned';
        documentTitle = 'RETURNED EQUIPMENT LIST';
      } else if (reportInfo.type === 'assignment') {
        reportCategory = 'assignment';
        documentTitle = 'ASSIGNMENT LIST';
      } else if (reportInfo.type === 'equipment_received') {
        reportCategory = 'equipment_received';
        documentTitle = 'RECEIVED EQUIPMENT LIST';
      } else if (reportInfo.type === 'equipment_created') {
        reportCategory = 'equipment_created';
        documentTitle = 'EQUIPMENT ADDED LIST';
      } else if (reportInfo.type === 'equipment_expired') {
        reportCategory = 'equipment_expired';
        documentTitle = 'EQUIPMENT EXPIRED LIST';
      } else if (reportInfo.type === 'equipment_damaged') {
        reportCategory = 'equipment_damaged';
        documentTitle = 'EQUIPMENT DAMAGED LIST';
      } else if (reportInfo.type === 'equipment_deleted') {
        reportCategory = 'equipment_deleted';
        documentTitle = 'EQUIPMENT DELETED LIST';
      } else if (reportInfo.type === 'equipment_restored') {
        reportCategory = 'equipment_restored';
        documentTitle = 'EQUIPMENT RESTORED LIST';
      }

      const reportPayload = {
        report_type: reportType,
        report_category: reportCategory,
        document_title: documentTitle,
        title: documentTitle,
        header_title: documentTitle,
        pdf_title: documentTitle,
        report_title: documentTitle,
        document_type: reportCategory,
        category: reportCategory,
        type: reportCategory
      };

      // Add appropriate IDs based on report type
      if (reportInfo.type === 'inspection' && inspectionIds.length > 0) {
        reportPayload.inspection_ids = inspectionIds;
      } else if ((reportInfo.type === 'assignment' || reportInfo.type === 'returned' || reportInfo.type === 'equipment_received') && assignmentIds.length > 0) {
        reportPayload.assignment_ids = assignmentIds;
      } else if (['equipment_created', 'equipment_expired', 'equipment_damaged', 'equipment_deleted', 'equipment_restored'].includes(reportInfo.type) && eventIds.length > 0) {
        // For new report types, send event IDs
        reportPayload.event_ids = eventIds;
      } else {
        // Fallback to equipment_ids if specific IDs not available
        reportPayload.equipment_ids = Array.from(equipmentIds);
      }
      
      console.log('📤 Sending report request:', reportPayload);
      
      const response = await axios.post(`${API_BASE_URL}/ppe-report/`, reportPayload, config);

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on report type
      const timestamp = new Date().toISOString().slice(0, 10);

      let reportTitle = 'PPE_Equipment_Report';
      if (reportInfo.type === 'inspection') {
        reportTitle = 'PPE_Inspection_Report';
      } else if (reportInfo.type === 'returned') {
        reportTitle = 'PPE_Returned_Report';
      } else if (reportInfo.type === 'assignment') {
        reportTitle = 'PPE_Assignment_Report';
      } else if (reportInfo.type === 'equipment_received') {
        reportTitle = 'PPE_Received_Report';
      } else if (reportInfo.type === 'equipment_created') {
        reportTitle = 'PPE_Added_Report';
      } else if (reportInfo.type === 'equipment_expired') {
        reportTitle = 'PPE_Expired_Report';
      } else if (reportInfo.type === 'equipment_damaged') {
        reportTitle = 'PPE_Damaged_Report';
      } else if (reportInfo.type === 'equipment_deleted') {
        reportTitle = 'PPE_Deleted_Report';
      } else if (reportInfo.type === 'equipment_restored') {
        reportTitle = 'PPE_Restored_Report';
      }

      const filename = filteredEntries.length === 1
        ? `${reportTitle}_${filteredEntries[0].equipment_id}_${timestamp}.pdf`
        : `${reportTitle}_${timestamp}.pdf`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // Clear selection after successful generation
      setSelectedEntries(new Set());
      setSelectAll(false);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Update selectAll state when filtered data changes
  useEffect(() => {
    if (paginatedData.length > 0) {
      setSelectAll(selectedEntries.size === paginatedData.length && selectedEntries.size > 0);
    } else {
      setSelectAll(false);
    }
  }, [paginatedData, selectedEntries]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span className="ml-2 text-slate-600">Loading equipment log...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-steel-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-50 to-steel-50 border-b border-steel-200 px-6 py-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-xl shadow-sm border border-steel-200">
            <History className="h-6 w-6 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Equipment Log Book</h1>
            <p className="text-sm text-slate-500">Complete history of all PPE equipment events and changes</p>
          </div>
        </div>
      </div>

      <div className="px-4">
        <div className="w-full mx-auto">

        {/* Filters */}
        <div className="bg-white rounded-xl border border-steel-200 p-4 mb-6">
          {/* First row - Search, Date From, Date To, User */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
              <div className="relative">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search equipment, model, serial, type, or user... (press Enter to search)"
                  className="pl-10 w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  autoFocus
                />
              </div>
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date From</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date To</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
              />
            </div>

            {/* User Filter - Collapsible Checkboxes with search */}
            <div ref={userRef}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                User {userFilter.length > 0 && `(${userFilter.length} selected)`}
              </label>
              <div className="border border-steel-300 rounded-xl">
                {/* Clickable header */}
                <button
                  onClick={() => setUserExpanded(!userExpanded)}
                  className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                >
                  <span className="text-sm text-slate-700">
                    {userFilter.length === 0 ? (
                      'Select users...'
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {userFilter.slice(0, 2).map(userId => {
                          const user = usersList.find(u => u.id.toString() === userId || (u.id === 'system' && userId === 'system'));
                          return user ? (
                            <span key={userId} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-brand-100 text-brand-700 text-xs rounded-full">
                              <User className="h-3 w-3" />
                              <span>{user.full_name || `${user.first_name} ${user.last_name}`}</span>
                            </span>
                          ) : null;
                        })}
                        {userFilter.length > 2 && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                            +{userFilter.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </span>
                  {userExpanded ? (
                    <ChevronUp className="h-4 w-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  )}
                </button>

                {/* Expandable content */}
                {userExpanded && (
                  <div className="border-t border-steel-200">
                    {/* Search input */}
                    <div className="p-2 border-b border-steel-200">
                      <input
                        type="text"
                        placeholder="Search users..."
                        className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {/* Checkbox list */}
                    <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                      {getFilteredUsers().map(user => {
                        const userId = user.id === 'system' ? 'system' : user.id.toString();
                        return (
                          <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={userFilter.includes(userId)}
                              onChange={() => handleUserToggle(user.id)}
                              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                            />
                            <User className="h-4 w-4 text-slate-500" />
                            <span className="text-sm">{user.full_name || `${user.first_name} ${user.last_name}`}</span>
                          </label>
                        );
                      })}
                      {getFilteredUsers().length === 0 && (
                        <div className="text-sm text-slate-500 py-2">No users found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Second row - Event Type and dynamic sub-filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Event Type Filter - Collapsible Single Select with search */}
            <div ref={eventTypeRef}>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Event Type
              </label>
              <div className="border border-steel-300 rounded-xl">
                {/* Clickable header */}
                <button
                  onClick={() => setEventTypeExpanded(!eventTypeExpanded)}
                  className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                >
                  <span className="text-sm text-slate-700">
                    {!eventTypeFilter ? (
                      'Select event type...'
                    ) : (
                      <div className="flex items-center space-x-1">
                        {(() => {
                          const type = eventTypes[eventTypeFilter];
                          const IconComponent = type?.icon || Package;
                          return (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-brand-100 text-brand-700 text-xs rounded-full">
                              <IconComponent className="h-3 w-3" />
                              <span>{type?.label || eventTypeFilter}</span>
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </span>
                  {eventTypeExpanded ? (
                    <ChevronUp className="h-4 w-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  )}
                </button>

                {/* Expandable content */}
                {eventTypeExpanded && (
                  <div className="border-t border-steel-200">
                    {/* Search input */}
                    <div className="p-2 border-b border-steel-200">
                      <input
                        type="text"
                        placeholder="Search event types..."
                        className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        value={eventTypeSearch}
                        onChange={(e) => setEventTypeSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {/* Checkbox list (single-select with deselect capability) */}
                    <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                      {getFilteredEventTypes().map(([key, type]) => {
                        const IconComponent = type.icon;
                        return (
                          <label key={key} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={eventTypeFilter === key}
                              onChange={() => handleEventTypeToggle(key)}
                              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                            />
                            <IconComponent className={`h-4 w-4 ${type.color}`} />
                            <span className="text-sm">{type.label}</span>
                          </label>
                        );
                      })}
                      {getFilteredEventTypes().length === 0 && (
                        <div className="text-sm text-slate-500 py-2">No event types found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Email Notification Sub-Filter - Show only when email_notifications is selected */}
            {eventTypeFilter === 'email_notifications' && (
              <div ref={emailNotificationRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Notification Types {emailNotificationFilter.length > 0 && `(${emailNotificationFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setEmailNotificationExpanded(!emailNotificationExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {emailNotificationFilter.length === 0 ? (
                        'Select notification types...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {emailNotificationFilter.slice(0, 2).map(key => {
                            const type = emailNotificationTypes[key];
                            return (
                              <span key={key} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                                <AlertCircle className="h-3 w-3" />
                                <span>{type?.label || key}</span>
                              </span>
                            );
                          })}
                          {emailNotificationFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{emailNotificationFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {emailNotificationExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {emailNotificationExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search notification types..."
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                          value={emailNotificationSearch}
                          onChange={(e) => setEmailNotificationSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                        {getFilteredEmailNotificationTypes().map(([key, type]) => {
                          return (
                            <label key={key} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={emailNotificationFilter.includes(key)}
                                onChange={() => handleEmailNotificationToggle(key)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-steel-300 rounded"
                              />
                              <AlertCircle className={`h-4 w-4 ${type.color}`} />
                              <span className="text-sm">{type.label}</span>
                            </label>
                          );
                        })}
                        {getFilteredEmailNotificationTypes().length === 0 && (
                          <div className="text-sm text-slate-500 py-2">No notification types found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Inspection Sub-Filter - Show only when inspection is selected */}
            {eventTypeFilter === 'inspection' && (
              <div ref={inspectionSubRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Inspection Result {inspectionSubFilter.length > 0 && `(${inspectionSubFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setInspectionSubExpanded(!inspectionSubExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {inspectionSubFilter.length === 0 ? (
                        'Select inspection results...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {inspectionSubFilter.slice(0, 2).map(key => {
                            const type = inspectionSubtypes[key];
                            const IconComponent = type?.icon || CheckCircle;
                            return (
                              <span key={key} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                <IconComponent className="h-3 w-3" />
                                <span>{type?.label || key}</span>
                              </span>
                            );
                          })}
                          {inspectionSubFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{inspectionSubFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {inspectionSubExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {inspectionSubExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search inspection results..."
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                          value={inspectionSubSearch}
                          onChange={(e) => setInspectionSubSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                        {Object.entries(inspectionSubtypes)
                          .filter(([key, type]) => type.label.toLowerCase().includes(inspectionSubSearch.toLowerCase()))
                          .map(([key, type]) => {
                            const IconComponent = type.icon;
                            return (
                              <label key={key} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                                <input
                                  type="checkbox"
                                  checked={inspectionSubFilter.includes(key)}
                                  onChange={() => handleInspectionSubToggle(key)}
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-steel-300 rounded"
                                />
                                <IconComponent className={`h-4 w-4 ${type.color}`} />
                                <span className="text-sm">{type.label}</span>
                              </label>
                            );
                          })}
                        {Object.entries(inspectionSubtypes)
                          .filter(([key, type]) => type.label.toLowerCase().includes(inspectionSubSearch.toLowerCase()))
                          .length === 0 && (
                          <div className="text-sm text-slate-500 py-2">No inspection types found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Status Changed Sub-Filter - Show only when status_changed is selected */}
            {eventTypeFilter === 'status_changed' && (
              <div ref={statusSubRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Status Type {statusSubFilter.length > 0 && `(${statusSubFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setStatusSubExpanded(!statusSubExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {statusSubFilter.length === 0 ? (
                        'Select status types...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {statusSubFilter.slice(0, 2).map(key => {
                            const type = statusSubtypes[key];
                            return (
                              <span key={key} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                                <Settings className="h-3 w-3" />
                                <span>{type?.label || key}</span>
                              </span>
                            );
                          })}
                          {statusSubFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{statusSubFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {statusSubExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {statusSubExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search status types..."
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                          value={statusSubSearch}
                          onChange={(e) => setStatusSubSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                        {Object.entries(statusSubtypes)
                          .filter(([key, type]) => type.label.toLowerCase().includes(statusSubSearch.toLowerCase()))
                          .map(([key, type]) => {
                            return (
                              <label key={key} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                                <input
                                  type="checkbox"
                                  checked={statusSubFilter.includes(key)}
                                  onChange={() => handleStatusSubToggle(key)}
                                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-steel-300 rounded"
                                />
                                <Settings className={`h-4 w-4 ${type.color}`} />
                                <span className="text-sm">{type.label}</span>
                              </label>
                            );
                          })}
                        {Object.entries(statusSubtypes)
                          .filter(([key, type]) => type.label.toLowerCase().includes(statusSubSearch.toLowerCase()))
                          .length === 0 && (
                          <div className="text-sm text-slate-500 py-2">No status types found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service Workshop Sub-Filter - Show only when service_shipment is selected */}
            {eventTypeFilter === 'service_shipment' && (
              <div ref={serviceWorkshopRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Service Workshop {serviceWorkshopFilter.length > 0 && `(${serviceWorkshopFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setServiceWorkshopExpanded(!serviceWorkshopExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {serviceWorkshopFilter.length === 0 ? (
                        'Select service companies...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {serviceWorkshopFilter.slice(0, 2).map(companyId => {
                            const company = serviceWorkshopsList.find(c => c.id === companyId);
                            return company ? (
                              <span key={companyId} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                <Package className="h-3 w-3" />
                                <span>{company.name}</span>
                              </span>
                            ) : null;
                          })}
                          {serviceWorkshopFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{serviceWorkshopFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {serviceWorkshopExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {serviceWorkshopExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search workshops..."
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                          value={serviceWorkshopSearch}
                          onChange={(e) => setServiceWorkshopSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                        {getFilteredServiceWorkshops().map(company => (
                          <label key={company.id} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={serviceWorkshopFilter.includes(company.id)}
                              onChange={() => handleServiceWorkshopToggle(company.id)}
                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-steel-300 rounded"
                            />
                            <Package className="h-4 w-4 text-red-500" />
                            <span className="text-sm">{company.name}</span>
                          </label>
                        ))}
                        {getFilteredServiceWorkshops().length === 0 && (
                          <div className="text-sm text-slate-500 py-2">No service companies found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service Direction Sub-Filter - Show only when service_shipment is selected */}
            {eventTypeFilter === 'service_shipment' && (
              <div ref={serviceDirectionRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Service Direction {serviceDirectionFilter.length > 0 && `(${serviceDirectionFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setServiceDirectionExpanded(!serviceDirectionExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {serviceDirectionFilter.length === 0 ? (
                        'Select direction...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {serviceDirectionFilter.map(direction => (
                            <span key={direction} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-brand-100 text-brand-700 text-xs rounded-full">
                              <Package className="h-3 w-3" />
                              <span>{direction === 'sent' ? 'Sent to Service' : 'Returned from Service'}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </span>
                    {serviceDirectionExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {serviceDirectionExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Checkbox list */}
                      <div className="p-2 space-y-1">
                        <label className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={serviceDirectionFilter.includes('sent')}
                            onChange={() => handleServiceDirectionToggle('sent')}
                            className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                          />
                          <Package className="h-4 w-4 text-brand-500" />
                          <span className="text-sm">Sent to Service</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={serviceDirectionFilter.includes('returned')}
                            onChange={() => handleServiceDirectionToggle('returned')}
                            className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                          />
                          <Package className="h-4 w-4 text-brand-500" />
                          <span className="text-sm">Returned from Service</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assignment Type Sub-Filter - Show only when assignment_created or assignment_returned is selected */}
            {(eventTypeFilter === 'assignment_created' || eventTypeFilter === 'assignment_returned') && (
              <div ref={assignmentTypeRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Assignment Type {assignmentTypeFilter.length > 0 && `(${assignmentTypeFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setAssignmentTypeExpanded(!assignmentTypeExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {assignmentTypeFilter.length === 0 ? (
                        'Select assignment type...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {assignmentTypeFilter.map(type => (
                            <span key={type} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                              <User className="h-3 w-3" />
                              <span>{type === 'user' ? 'User Assignment' : 'Project Assignment'}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </span>
                    {assignmentTypeExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {assignmentTypeExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Checkbox list */}
                      <div className="p-2 space-y-1">
                        <label className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={assignmentTypeFilter.includes('user')}
                            onChange={() => handleAssignmentTypeToggle('user')}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-steel-300 rounded"
                          />
                          <User className="h-4 w-4 text-indigo-500" />
                          <span className="text-sm">User Assignment</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={assignmentTypeFilter.includes('project')}
                            onChange={() => handleAssignmentTypeToggle('project')}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-steel-300 rounded"
                          />
                          <Users className="h-4 w-4 text-indigo-500" />
                          <span className="text-sm">Project Assignment</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assigned User Filter - Show only when assignment events are selected */}
            {(eventTypeFilter === 'assignment_created' || eventTypeFilter === 'assignment_returned') && availableAssignedUsersList.length > 0 && (
              <div ref={assignedUserRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Users {assignedUserFilter.length > 0 && `(${assignedUserFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setAssignedUserExpanded(!assignedUserExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {assignedUserFilter.length === 0 ? (
                        'Select users...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {assignedUserFilter.slice(0, 2).map(userId => {
                            const user = availableAssignedUsersList.find(u => u.id === userId);
                            return user ? (
                              <span key={userId} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-brand-100 text-brand-700 text-xs rounded-full">
                                <User className="h-3 w-3" />
                                <span>{user.name}</span>
                              </span>
                            ) : null;
                          })}
                          {assignedUserFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{assignedUserFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {assignedUserExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {assignedUserExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={assignedUserSearch}
                          onChange={(e) => setAssignedUserSearch(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="p-2 max-h-64 overflow-y-auto space-y-1">
                        {availableAssignedUsersList
                          .filter(user => user.name.toLowerCase().includes(assignedUserSearch.toLowerCase()))
                          .map(user => (
                            <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={assignedUserFilter.includes(user.id)}
                                onChange={() => handleAssignedUserToggle(user.id)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-steel-300 rounded"
                              />
                              <User className="h-4 w-4 text-brand-500" />
                              <span className="text-sm">{user.name}</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Assigned Project Filter - Show only when assignment events are selected */}
            {(eventTypeFilter === 'assignment_created' || eventTypeFilter === 'assignment_returned') && availableAssignedProjectsList.length > 0 && (
              <div ref={assignedProjectRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Projects {assignedProjectFilter.length > 0 && `(${assignedProjectFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setAssignedProjectExpanded(!assignedProjectExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {assignedProjectFilter.length === 0 ? (
                        'Select projects...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {assignedProjectFilter.slice(0, 2).map(projectId => {
                            const project = availableAssignedProjectsList.find(p => p.id === projectId);
                            return project ? (
                              <span key={projectId} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                <Users className="h-3 w-3" />
                                <span>{project.name}</span>
                              </span>
                            ) : null;
                          })}
                          {assignedProjectFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{assignedProjectFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {assignedProjectExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {assignedProjectExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search projects..."
                          value={assignedProjectSearch}
                          onChange={(e) => setAssignedProjectSearch(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="p-2 max-h-64 overflow-y-auto space-y-1">
                        {availableAssignedProjectsList
                          .filter(project => project.name.toLowerCase().includes(assignedProjectSearch.toLowerCase()))
                          .map(project => (
                            <label key={project.id} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={assignedProjectFilter.includes(project.id)}
                                onChange={() => handleAssignedProjectToggle(project.id)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-steel-300 rounded"
                              />
                              <Users className="h-4 w-4 text-green-500" />
                              <span className="text-sm">{project.name}</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Received User Filter - Show only when equipment_received event is selected */}
            {eventTypeFilter === 'equipment_received' && availableReceivedUsersList.length > 0 && (
              <div ref={receivedUserRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Users {receivedUserFilter.length > 0 && `(${receivedUserFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setReceivedUserExpanded(!receivedUserExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {receivedUserFilter.length === 0 ? (
                        'Select users...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {receivedUserFilter.slice(0, 2).map(userId => {
                            const user = availableReceivedUsersList.find(u => u.id === userId);
                            return user ? (
                              <span key={userId} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded-full">
                                <User className="h-3 w-3" />
                                <span>{user.name}</span>
                              </span>
                            ) : null;
                          })}
                          {receivedUserFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{receivedUserFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {receivedUserExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {receivedUserExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={receivedUserSearch}
                          onChange={(e) => setReceivedUserSearch(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="p-2 max-h-64 overflow-y-auto space-y-1">
                        {availableReceivedUsersList
                          .filter(user => user.name.toLowerCase().includes(receivedUserSearch.toLowerCase()))
                          .map(user => (
                            <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={receivedUserFilter.includes(user.id)}
                                onChange={() => handleReceivedUserToggle(user.id)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-steel-300 rounded"
                              />
                              <User className="h-4 w-4 text-cyan-500" />
                              <span className="text-sm">{user.name}</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Inspection for Company Filter - Show only for companies with can_inspection=true and when inspection is selected */}
            {(() => {
              const hasCanInspection = user?.company_can_inspection === true;
              const hasInspectionFilter = eventTypeFilter === 'inspection';
              const hasCompanies = cooperationCompaniesList.length > 0;
              const shouldShow = hasCanInspection && hasInspectionFilter && hasCompanies;

              console.log('🔍 Inspection for Company Filter Conditions:', {
                user,
                hasCanInspection,
                hasInspectionFilter,
                hasCompanies,
                cooperationCompaniesList,
                eventTypeFilter,
                shouldShow
              });

              return shouldShow;
            })() && (
              <div ref={ownerCompanyRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Inspection for Company {ownerCompanyFilter.length > 0 && `(${ownerCompanyFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setOwnerCompanyExpanded(!ownerCompanyExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {ownerCompanyFilter.length === 0 ? (
                        'Select companies...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {ownerCompanyFilter.slice(0, 2).map(companyId => {
                            const company = cooperationCompaniesList.find(c => c.id === companyId);
                            return company ? (
                              <span key={companyId} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                <Shield className="h-3 w-3" />
                                <span>{company.name}</span>
                              </span>
                            ) : null;
                          })}
                          {ownerCompanyFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{ownerCompanyFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {ownerCompanyExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {ownerCompanyExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search companies..."
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                          value={ownerCompanySearch}
                          onChange={(e) => setOwnerCompanySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                        {getFilteredOwnerCompanies().map(company => (
                          <label key={company.id} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={ownerCompanyFilter.includes(company.id)}
                              onChange={() => handleOwnerCompanyToggle(company.id)}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-steel-300 rounded"
                            />
                            <Shield className="h-4 w-4 text-purple-600" />
                            <span className="text-sm">{company.name}</span>
                          </label>
                        ))}
                        {getFilteredOwnerCompanies().length === 0 && (
                          <div className="text-sm text-slate-500 py-2">No companies found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Inspection from Company Filter - Show for companies WITHOUT can_inspection (opposite of "Inspection for Company") */}
            {eventTypeFilter === 'inspection' && user?.company_can_inspection !== true && (
              <div ref={inspectorCompanyRef}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Inspection from Company {inspectorCompanyFilter.length > 0 && `(${inspectorCompanyFilter.length} selected)`}
                </label>
                <div className="border border-steel-300 rounded-xl">
                  {/* Clickable header */}
                  <button
                    onClick={() => setInspectorCompanyExpanded(!inspectorCompanyExpanded)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                  >
                    <span className="text-sm text-slate-700">
                      {inspectorCompanyFilter.length === 0 ? (
                        'Select companies...'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {inspectorCompanyFilter.slice(0, 2).map(companyId => {
                            const allCompanies = getFilteredInspectorCompanies();
                            const company = allCompanies.find(c => c.id === companyId);
                            return company ? (
                              <span key={companyId} className="inline-flex items-center space-x-1 px-2 py-0.5 bg-brand-100 text-brand-700 text-xs rounded-full">
                                <Building className="h-3 w-3" />
                                <span>{company.name}</span>
                              </span>
                            ) : null;
                          })}
                          {inspectorCompanyFilter.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-steel-100 text-slate-600 text-xs rounded-full">
                              +{inspectorCompanyFilter.length - 2} more
                            </span>
                          )}
                        </div>
                      )}
                    </span>
                    {inspectorCompanyExpanded ? (
                      <ChevronUp className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    )}
                  </button>

                  {/* Expandable content */}
                  {inspectorCompanyExpanded && (
                    <div className="border-t border-steel-200">
                      {/* Search input */}
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search companies..."
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                          value={inspectorCompanySearch}
                          onChange={(e) => setInspectorCompanySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {/* Checkbox list */}
                      <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                        {getFilteredInspectorCompanies().map(company => (
                          <label key={company.id} className="flex items-center space-x-2 cursor-pointer hover:bg-steel-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={inspectorCompanyFilter.includes(company.id)}
                              onChange={() => handleInspectorCompanyToggle(company.id)}
                              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                            />
                            <Building className="h-4 w-4 text-brand-600" />
                            <span className="text-sm">{company.name}</span>
                          </label>
                        ))}
                        {getFilteredInspectorCompanies().length === 0 && (
                          <div className="text-sm text-slate-500 py-2">No companies found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reset Filters Button */}
            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 bg-steel-100 text-slate-700 rounded-xl hover:bg-steel-200 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>


          {/* Results Summary */}
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>Showing {logData.length} entries on this page (Total: {totalCount} entries)</span>
            <div className="flex items-center space-x-2">
              <span>Items per page:</span>
              <select
                className="border border-steel-300 rounded-xl pl-2 pr-8 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>
          </div>
        </div>

        {/* Selection Controls */}
        {paginatedData.length > 0 && (
          <div className="bg-white rounded-xl border border-steel-200 p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="select-all"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                  />
                  <label htmlFor="select-all" className="ml-2 text-sm font-medium text-slate-700">
                    Select All Entries on Page ({paginatedData.length} items)
                  </label>
                </div>
                {selectedEntries.size > 0 && !selectAll && (
                  <span className="text-sm text-brand-600 font-medium">
                    {selectedEntries.size} entries selected ({getSelectedEquipmentIds().length} unique equipment)
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                {getAvailableReportButtons().map((reportButton) => (
                  <button
                    key={reportButton.type}
                    onClick={() => handleGenerateReport(reportButton)}
                    disabled={isGeneratingReport}
                    className="flex items-center space-x-2 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-brand-500/20"
                  >
                    {isGeneratingReport ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4" />
                        <span>{reportButton.label}</span>
                        <Download className="h-4 w-4" />
                      </>
                    )}
                  </button>
                ))}

                {/* Inspection Cost Report Button - only for companies with can_inspection */}
                {shouldShowInspectionCostButton() && (
                  <button
                    onClick={handleGenerateInspectionCostReport}
                    disabled={isGeneratingReport}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isGeneratingReport ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4" />
                        <span>Inspection Cost</span>
                        <Download className="h-4 w-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Log Table */}
        <div className="bg-white rounded-xl border border-steel-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-steel-100 border-b-2 border-brand-500">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-12">
                    <span>Select</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('timestamp')}
                      className="flex items-center space-x-1 hover:text-slate-700"
                    >
                      <span>Date & Time</span>
                      {getSortIcon('timestamp')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('equipment_name')}
                      className="flex items-center space-x-1 hover:text-slate-700"
                    >
                      <span>Equipment</span>
                      {getSortIcon('equipment_name')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Event Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('user_name')}
                      className="flex items-center space-x-1 hover:text-slate-700"
                    >
                      <span>User</span>
                      {getSortIcon('user_name')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-steel-200">
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No log entries found matching your filters
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((entry) => {
                    const eventTypeConfig = eventTypes[entry.event_type] || { 
                      label: entry.event_type, 
                      color: 'text-slate-600', 
                      icon: Clock 
                    };
                    const IconComponent = eventTypeConfig.icon;
                    
                    return (
                      <tr key={entry.id} className="hover:bg-brand-50 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedEntries.has(entry.id)}
                            onChange={() => handleEntrySelect(entry.id)}
                            className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-steel-300 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900">
                          {formatDateTime(entry.timestamp)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-900">{entry.equipment_name}</div>
                          <div className="text-sm text-slate-500">SN - {entry.equipment_serial}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`flex items-center space-x-2 ${eventTypeConfig.color}`}>
                            <IconComponent className="h-4 w-4" />
                            <span className="text-sm font-medium">{eventTypeConfig.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900">
                          {entry.user_name}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {(() => {
                            // Special handling for service_shipment to differentiate send vs return
                            if (entry.event_type === 'service_shipment' && entry.details) {
                              const details = typeof entry.details === 'object' ? entry.details : {};
                              const fromStatus = details.from_status || '';
                              const equipmentName = details.equipment_name || entry.equipment_name || 'Equipment';
                              const serviceCompany = details.service_company_name || 'service';

                              // If from_status is 'service', it's a return; otherwise it's a send
                              if (fromStatus === 'service') {
                                return `${equipmentName} returned from ${serviceCompany}`;
                              } else {
                                return `${equipmentName} sent to ${serviceCompany}`;
                              }
                            }
                            // For all other event types, use original description
                            return entry.description;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {entry.details && (
                            <div className="text-xs">
                              {typeof entry.details === 'object' ? (
                                // Handle object details (original format)
                                <div className="space-y-1">
                                  {entry.event_type === 'assignment_created' ? (
                                    // Special formatting for assignment created events
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {(entry.details.assigned_to_user_name || entry.details.assigned_to_project_name) && (
                                        <div>
                                          <span className="font-medium">Assigned To:</span> {entry.details.assigned_to_user_name || entry.details.assigned_to_project_name}
                                        </div>
                                      )}
                                      {entry.details.assignment_date && (
                                        <div>
                                          <span className="font-medium">Assignment Date:</span> {entry.details.assignment_date}
                                        </div>
                                      )}
                                      {entry.details.group_name && (
                                        <div>
                                          <span className="font-medium">Group:</span> {entry.details.group_name}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'assignment_returned' ? (
                                    // Special formatting for assignment returned events
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {(entry.details.returned_by_user_name || entry.details.assigned_to_project_name) && (
                                        <div>
                                          <span className="font-medium">Returned From:</span> {entry.details.returned_by_user_name || entry.details.assigned_to_project_name}
                                        </div>
                                      )}
                                      {entry.details.returned_to_user_name && (
                                        <div>
                                          <span className="font-medium">Returned To:</span> {entry.details.returned_to_user_name}
                                        </div>
                                      )}
                                      {entry.details.return_date && (
                                        <div>
                                          <span className="font-medium">Return Date:</span> {entry.details.return_date}
                                        </div>
                                      )}
                                      {entry.details.duration_days !== null && entry.details.duration_days !== undefined && (
                                        <div>
                                          <span className="font-medium">Duration:</span> {entry.details.duration_days} days
                                        </div>
                                      )}
                                      {entry.details.return_condition && (
                                        <div>
                                          <span className="font-medium">Return Condition:</span> {entry.details.return_condition}
                                        </div>
                                      )}
                                      {entry.details.notes && (
                                        <div>
                                          <span className="font-medium">Notes:</span> {entry.details.notes}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'equipment_created' ? (
                                    // Special formatting for equipment created events
                                    <div className="space-y-1">
                                      {entry.details.equipment_model && (
                                        <div>
                                          <span className="font-medium">Equipment Model:</span> {entry.details.equipment_model}
                                        </div>
                                      )}
                                      {entry.details.equipment_type && (
                                        <div>
                                          <span className="font-medium">Equipment Type:</span> {entry.details.equipment_type}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.manufacture_date && (
                                        <div>
                                          <span className="font-medium">Manufacture Date:</span> {entry.details.manufacture_date}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'equipment_received' ? (
                                    // Special formatting for equipment received events
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.received_by_user_name && (
                                        <div>
                                          <span className="font-medium">Received By:</span> {entry.details.received_by_user_name}
                                        </div>
                                      )}
                                      {entry.details.received_date && (
                                        <div>
                                          <span className="font-medium">Received Date:</span> {entry.details.received_date}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'equipment_deleted' ? (
                                    // Special formatting for equipment deleted events
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.from_status && (
                                        <div>
                                          <span className="font-medium">Previous Status:</span> {entry.details.from_status}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'equipment_restored' ? (
                                    // Special formatting for equipment restored events
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.from_status && (
                                        <div>
                                          <span className="font-medium">From Status:</span> {entry.details.from_status}
                                        </div>
                                      )}
                                      {entry.details.to_status && (
                                        <div>
                                          <span className="font-medium">Restored To:</span> {entry.details.to_status}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'equipment_damaged' ? (
                                    // Special formatting for equipment damaged events
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.from_status && (
                                        <div>
                                          <span className="font-medium">Previous Status:</span> {entry.details.from_status}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'equipment_expired' ? (
                                    // Special formatting for equipment expired events
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.from_status && (
                                        <div>
                                          <span className="font-medium">Previous Status:</span> {entry.details.from_status}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'inspection_passed' || entry.event_type === 'inspection_failed' || entry.event_type === 'inspection' || (entry.event_type === 'equipment_updated' && (entry.details.result || entry.details.validity_date || entry.details.inspector_name || entry.details.external_inspector)) ? (
                                    // Special formatting for inspection events (including external inspections logged as equipment_updated)
                                    <div className="space-y-1">
                                      {(entry.details.inspector_name || entry.details.external_inspector) && (
                                        <div>
                                          <span className="font-medium">Inspector Name:</span> {entry.details.inspector_name || entry.details.external_inspector}
                                        </div>
                                      )}
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.equipment_owner_company_name && entry.details.equipment_owner_company_id !== user.company && (
                                        <div>
                                          <span className="font-medium">Owner Company:</span> {entry.details.equipment_owner_company_name}
                                        </div>
                                      )}
                                      {entry.details.result && (
                                        <div>
                                          <span className="font-medium">Result:</span> {entry.details.result}
                                        </div>
                                      )}
                                      {entry.details.validity_date && (
                                        <div>
                                          <span className="font-medium">Next Inspection:</span> {entry.details.validity_date}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'service_shipment' ? (
                                    // Special formatting for service shipment events
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.service_company_name && (
                                        <div>
                                          <span className="font-medium">Service Company:</span> {entry.details.service_company_name}
                                        </div>
                                      )}
                                      {entry.details.from_status && (
                                        <div>
                                          <span className="font-medium">Previous Status:</span> {entry.details.from_status}
                                        </div>
                                      )}
                                    </div>
                                  ) : entry.event_type === 'equipment_updated' && entry.details.changed_fields ? (
                                    // Special formatting for equipment_updated events (non-inspection)
                                    <div className="space-y-1">
                                      {entry.details.equipment_name && (
                                        <div>
                                          <span className="font-medium">Equipment Name:</span> {entry.details.equipment_name}
                                        </div>
                                      )}
                                      {entry.details.equipment_serial && (
                                        <div>
                                          <span className="font-medium">Equipment Serial:</span> {entry.details.equipment_serial}
                                        </div>
                                      )}
                                      {entry.details.changed_fields && (
                                        <div>
                                          <span className="font-medium">Changed Fields:</span> {Array.isArray(entry.details.changed_fields) ? entry.details.changed_fields.join(', ') : entry.details.changed_fields}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    // General formatting for other event types
                                    Object.entries(entry.details).map(([key, value]) => (
                                      value && (
                                        <div key={key}>
                                          <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {value}
                                        </div>
                                      )
                                    ))
                                  )}
                                </div>
                              ) : (
                                // Handle string details (notification logs)
                                <div className="whitespace-pre-wrap">{entry.details}</div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-steel-50 border-t border-steel-200 flex items-center justify-between">
              <div className="flex items-center text-sm text-slate-700">
                <span>
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of{' '}
                  {totalCount} results
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-xl border border-steel-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-steel-50 hover:border-brand-300 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-3 py-1 text-sm text-slate-700">
                  {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-xl border border-steel-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-steel-50 hover:border-brand-300 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default EquipmentLogBook;